const CDN_BASE_URL =
  "https://sqlvaaziimhebqn7as.blob.core.windows.net/chaloapp/vehicleImages/";

const resolvedUrlCache = new Map<string, string | null>();

/** Naming variations used when resolving CDN filenames (matches native app). */
function nameVariations(value: string): string[] {
  const trimmed = value.trim();
  if (!trimmed) return [];

  const variations: string[] = [];
  const seen = new Set<string>();

  const add = (candidate: string) => {
    if (candidate && !seen.has(candidate)) {
      seen.add(candidate);
      variations.push(candidate);
    }
  };

  add(trimmed.replace(/\s+/g, "").replace(/[^a-zA-Z0-9]/g, ""));

  const lowercase = trimmed
    .replace(/\s+/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  add(lowercase);

  add(
    trimmed
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "")
  );

  return variations;
}

function buildCandidateUrls(make: string, model: string): string[] {
  const urls: string[] = [];
  const seen = new Set<string>();

  for (const makePart of nameVariations(make)) {
    for (const modelPart of nameVariations(model)) {
      const url = `${CDN_BASE_URL}${makePart}_${modelPart}.png`;
      if (!seen.has(url)) {
        seen.add(url);
        urls.push(url);
      }
    }
  }

  return urls;
}

function canLoadImage(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

/**
 * Resolves a vehicle image URL from CDN by trying make/model filename variations.
 * Returns null when no image exists (caller should show a fallback).
 */
export async function resolveVehicleImageUrl(
  make: string,
  model: string
): Promise<string | null> {
  if (!make.trim() || !model.trim()) return null;

  const cacheKey = `${make.trim().toLowerCase()}|${model.trim().toLowerCase()}`;
  if (resolvedUrlCache.has(cacheKey)) {
    return resolvedUrlCache.get(cacheKey) ?? null;
  }

  for (const url of buildCandidateUrls(make, model)) {
    if (await canLoadImage(url)) {
      resolvedUrlCache.set(cacheKey, url);
      return url;
    }
  }

  resolvedUrlCache.set(cacheKey, null);
  return null;
}
